import { type FunctionComponent, useMemo } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';

import { SingleSelect, type SelectOption } from 'dibs-elements/exports/SingleSelect.tsx';

import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';

import { type DesignerDirectoryLocationFilter_viewer$data } from './__generated__/DesignerDirectoryLocationFilter_viewer.graphql.ts';

const messages = defineMessages({
    placeholder: {
        id: 'abt.DesignerDirectoryLocationFilter.placeholder',
        defaultMessage: 'Location',
    },
});

type Props = {
    locationSelection?: string;
    onLocationFilterChange: (arg: string | null) => void;
    viewer: DesignerDirectoryLocationFilter_viewer$data;
};

export const DesignerDirectoryLocationFilterComponent: FunctionComponent<Props> = ({
    locationSelection,
    onLocationFilterChange,
    viewer,
}: Props) => {
    const intl = useIntl();
    const options = useMemo<SelectOption[]>(
        () =>
            (viewer?.regions || [])
                .map(region =>
                    region && region.code && region.regionName
                        ? {
                              label: region.regionName,
                              value: region.code.toLowerCase(),
                          }
                        : null
                )
                .filter(filterNulls)
                .sort((a, b) => {
                    if (a.label < b.label) {
                        return -1;
                    }
                    if (a.label > b.label) {
                        return 1;
                    }
                    return 0;
                }),
        [viewer]
    );

    return (
        <SingleSelect
            searchable
            clearable
            options={Array.isArray(options) ? options : []}
            dataTn="design-firms-location-filter"
            name="design-firms-location-filter"
            label={
                <FormattedMessage
                    id="abt.DesignerDirectory.locationFilterLabel"
                    defaultMessage="Filter By"
                />
            }
            noResultsPrompt={
                <FormattedMessage
                    id="abt.DesignerDirectoryLocationFilter.noResults"
                    defaultMessage="No Results Found"
                />
            }
            onChange={location => onLocationFilterChange(location ? location.value : null)}
            placeholder={intl.formatMessage(messages.placeholder)}
            value={locationSelection}
            size="medium"
        />
    );
};

export const DesignerDirectoryLocationFilter = createFragmentContainer(
    DesignerDirectoryLocationFilterComponent,
    {
        viewer: graphql`
            fragment DesignerDirectoryLocationFilter_viewer on Viewer {
                regions(regionType: TRADE_FIRM_REGION) {
                    code
                    regionName
                }
            }
        `,
    }
);
